import { useEffect } from "react";
import { BUSINESS } from "./constants";

/**
 * Google Ads "calls from website" conversion tracking.
 *
 * - Uses the existing sitewide gtag (AW-18330308052). Does NOT load a second tag.
 * - Registers phone_conversion_number + phone_conversion_callback once.
 * - When Google supplies a forwarding number, we rewrite every visible phone
 *   element on the page (text + tel: href). If Google never calls back
 *   (ineligible visitor, blocker, no session), the real number stays.
 * - A MutationObserver reapplies the swap to elements rendered after
 *   client-side navigation.
 *
 * We intentionally do NOT touch JSON-LD, meta tags, form payloads, or the
 * BUSINESS constants — those keep the real NAP number.
 */

const REAL_PHONE_DISPLAY = BUSINESS.phone; // "(352) 901-3094"
const REAL_PHONE_HREF = BUSINESS.phoneHref; // "tel:+13529013094"
const REAL_PHONE_DIGITS_10 = "3529013094";
const REAL_PHONE_DIGITS_11 = "13529013094";
void REAL_PHONE_DISPLAY;
void REAL_PHONE_HREF;

// Match common visible formats of the real number.
const DISPLAY_PATTERNS: RegExp[] = [
  /\(352\)\s*901-3094/g,
  /352[.\s-]?901[.\s-]?3094/g,
  /\+1\s*\(?352\)?[.\s-]?901[.\s-]?3094/g,
];

function digitsOnly(s: string): string {
  return s.replace(/\D+/g, "");
}

function isRealPhoneHref(href: string | null): boolean {
  if (!href) return false;
  if (!href.toLowerCase().startsWith("tel:")) return false;
  const d = digitsOnly(href);
  return d === REAL_PHONE_DIGITS_10 || d === REAL_PHONE_DIGITS_11;
}

function elementLooksLikePhone(el: Element): boolean {
  const text = (el.textContent || "").trim();
  if (!text) return false;
  return DISPLAY_PATTERNS.some(rx => {
    rx.lastIndex = 0;
    return rx.test(text);
  });
}

function replaceTextIn(node: Node, forwardingDisplay: string) {
  if (node.nodeType === Node.TEXT_NODE) {
    const original = node.nodeValue || "";
    let updated = original;
    for (const rx of DISPLAY_PATTERNS) {
      rx.lastIndex = 0;
      updated = updated.replace(rx, forwardingDisplay);
    }
    if (updated !== original) node.nodeValue = updated;
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  // Skip script/style/JSON-LD.
  const tag = el.tagName;
  if (tag === "SCRIPT" || tag === "STYLE" || tag === "NOSCRIPT") return;
  for (const child of Array.from(el.childNodes)) {
    replaceTextIn(child, forwardingDisplay);
  }
}

function applyForwardingNumber(forwardingRaw: string) {
  if (!forwardingRaw) return;
  const forwardingDigits = digitsOnly(forwardingRaw);
  if (!forwardingDigits || forwardingDigits === REAL_PHONE_DIGITS_10 || forwardingDigits === REAL_PHONE_DIGITS_11) return;

  // Format for display: (XXX) XXX-XXXX if 10 digits, else use as-provided.
  const d = forwardingDigits.length === 11 && forwardingDigits.startsWith("1")
    ? forwardingDigits.slice(1)
    : forwardingDigits;
  const forwardingDisplay = d.length === 10
    ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
    : forwardingRaw.trim();
  const forwardingHref = `tel:${d}`;

  // 1. Update every tel: link that points to the real number.
  const anchors = document.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"], a[href^="TEL:"]');
  anchors.forEach(a => {
    if (!isRealPhoneHref(a.getAttribute("href"))) return;
    a.setAttribute("href", forwardingHref);
    // Rewrite text nodes inside the anchor.
    replaceTextIn(a, forwardingDisplay);
    a.setAttribute("data-dr-call-tracked", "1");
  });

  // 2. Update visible text elements elsewhere (footer copy, hero subtitles, etc.).
  //    Walk text nodes under <body>, skipping script/style. Cheap on this site.
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!parent) return NodeFilter.FILTER_REJECT;
      const t = parent.tagName;
      if (t === "SCRIPT" || t === "STYLE" || t === "NOSCRIPT") return NodeFilter.FILTER_REJECT;
      const v = node.nodeValue || "";
      if (!v) return NodeFilter.FILTER_REJECT;
      return DISPLAY_PATTERNS.some(rx => { rx.lastIndex = 0; return rx.test(v); })
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });
  const textNodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) textNodes.push(n as Text);
  textNodes.forEach(tn => replaceTextIn(tn, forwardingDisplay));
}

export function CallTracking() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as unknown as {
      __drForwardingNumber?: string;
      __drApplyForwarding?: (n: string) => void;
    };

    // The gtag phone-conversion config lives in <head> (see __root.tsx) so it
    // runs on first paint. It calls back into this applier.
    w.__drApplyForwarding = applyForwardingNumber;

    // If Google already provided a forwarding number on a prior page (SPA nav),
    // re-apply it to freshly rendered elements.
    const reapply = () => {
      const f = w.__drForwardingNumber;
      if (f) applyForwardingNumber(f);
    };
    reapply();

    // Observe DOM for new phone elements rendered after route changes.
    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        const f = w.__drForwardingNumber;
        if (!f) return;
        applyForwardingNumber(f);
      });
    };
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.type === "childList" && m.addedNodes.length) {
          for (const node of Array.from(m.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as Element;
              if (
                el.matches?.('a[href^="tel:"], a[href^="TEL:"]') ||
                el.querySelector?.('a[href^="tel:"], a[href^="TEL:"]') ||
                elementLooksLikePhone(el)
              ) {
                schedule();
                return;
              }
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
